/**
 * Mobile onboarding — "push the helm forward = the sails open and the ship goes".
 *
 * THE PROBLEM (owner report): on desktop, pushing forward (W / up-arrow) already raises the sail — the movement
 * key IS the sail control (see hud.ts sampleInput: W ramps `sail` up, S down). On mobile the helm joystick only
 * steers (a 1-D left/right rudder) and the sail lives on a SEPARATE ⛵ button a newcomer never discovers, so a
 * furled ship just sits there and the player bounces. This mirrors the desktop behaviour on the touch helm:
 * shoving the knob FORWARD (up) auto-hoists the sails from furled so the boat actually starts moving.
 *
 * PURE input-synthesis, no DOM and no wire: the result is written into the same `sail` (→ C_INPUT.sailTarget)
 * a thumb produces, the server stays authoritative, the deterministic sim is untouched.
 *
 * Manual-trim safety (owner rule "só auto-iça quando a vela está baixada/zero"): the auto-hoist ONLY fires when
 * the sail is furled (≤ FURLED_MAX). Once any sail is set — by the ⛵ button, the W/S keys, or a previous hoist —
 * the forward push no longer touches it, so a captain who deliberately trims to half-sail (or reefs down while
 * still pushing forward) is never overridden: the joystick guarantees a floor from furled, but the captain can
 * always reduce afterwards. Pure sideways steering (forward within the deadzone) never hoists.
 */

export interface HelmSailCfg {
  /** Forward-axis magnitude (0..1, +1 = knob pushed fully forward/up) that counts as a deliberate "go" push. */
  FORWARD_DEADZONE: number;
  /** Only auto-hoist while the sail is at/below this (furled) — the guard that never fights a deliberate trim. */
  FURLED_MAX: number;
  /** Sail level a forward push hoists a furled ship to (full sail — mirrors holding W to the top). */
  HOIST_TO: number;
}

export const HELM_SAIL: HelmSailCfg = {
  FORWARD_DEADZONE: 0.4,
  FURLED_MAX: 0.05,
  HOIST_TO: 1,
};

/**
 * Given the helm's forward-axis intent (`forward` ∈ [-1, 1], +1 = pushed fully forward) and the current sail
 * (0..1), return the sail to command this tick. Raises a FURLED ship to full on a forward push; otherwise the
 * sail is returned unchanged (so an already-trimmed rig is never overridden). Pure.
 */
export function helmAutoSail(forward: number, sail: number, cfg: HelmSailCfg = HELM_SAIL): number {
  if (forward > cfg.FORWARD_DEADZONE && sail <= cfg.FURLED_MAX) return cfg.HOIST_TO;
  return sail;
}

/**
 * Should a forward helm push also weigh anchor? Only when the ship is anchored AND the captain deliberately
 * pushes forward — "push forward = the ship moves" is a broken promise if a dropped anchor pins it. Pure.
 */
export function helmWeighAnchor(forward: number, anchored: boolean, cfg: HelmSailCfg = HELM_SAIL): boolean {
  return anchored && forward > cfg.FORWARD_DEADZONE;
}
